/// <amd-module name="Search/Code/Components/Pivot" />

import * as React from "react";
import * as Constants from "Search/Code/Constants";
import * as Resources from "Search/Code/Resources";
import { Filter } from "SearchUI/Utilities/Filter"
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssSearchService } from "VSS/Platform/Search";
import { ActionCreator } from "Search/Code/Flux/ActionCreator";
import { StoresHub } from "Search/Code/Flux/StoresHub";
import { ActionsHub } from "Search/Code/Flux/ActionsHub";
import { CodeSearchSource } from "Search/Code/Flux/Sources/CodeSearchSource";
import { IVssHistoryService, INavigationStateChangedEvent } from "VSS/Platform/History";
import { ResultsContainer } from "Search/Code/Components/Results";
import { getTFSPageData, ITfsPageData } from "Tfs/Platform/Page";
import { serializeFilters, getWindowTitle } from "Search/Shared/Utils";
import { TelemetryWriter } from "Search/Code/Flux/Sources/TelemetryWriter";
import { TelemetrySpy } from "Search/Code/Flux/Sources/TelemetrySpy";
import { CountSpy } from "Search/Code/Flux/Sources/CountSpy";
import { handlePivotNotification } from "Search/Code/Components/Notification";
import { handlePivotCommands } from "Search/Code/Components/PivotCommands";
import { applyNavigatedUrl, updateUrl } from "Search/Code/NavigationHandler";
import { ITabContext, IInputQuery } from "Search/Shared/Base/Types";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { PreferenceSpy } from "Search/Code/Flux/Sources/PreferenceSpy";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { CompositeStore } from "Search/Shared/Base/Stores/CompositeStoresManager";
import { PivotStateServiceSpy } from "Search/Code/Flux/Sources/PivotStateServiceSpy";
import { ISearchPivotStateService } from "Search/Shared/Base/PivotStateService";
import { EntityTypeUrlParam } from "Search/Code/Constants";
import { BasePivot } from "Search/Shared/Components/BasePivot";
import { SearchQuery } from "Search/LegacyWebApi/Contracts/Code";
import { CodeRefreshInfoSource } from "../Flux/Sources/CodeRefreshInfoSource";

export class CodeSearchPivot extends BasePivot {
    private readonly actionCreator: ActionCreator;
    private readonly storesHub: StoresHub;
    private readonly pageData: ITfsPageData | undefined;
    private readonly telemetryWriter: TelemetryWriter;
    private readonly telemetrySpy: TelemetrySpy;
    private readonly historyService: IVssHistoryService;
    private readonly preferenceSpy: PreferenceSpy;
    private readonly countSpy: CountSpy;
    private readonly serverSortEnabled: boolean;
    private readonly includeFacets: boolean;
    private readonly pivotStateServiceSpy: PivotStateServiceSpy;
    private readonly pivotStateService: ISearchPivotStateService;
    private disposeActions: (() => void)[];

    constructor(props: ITabContext, context: IVssComponentContext) {
        super(props, Constants.EntityTypeUrlParam, context);
        this.pageData = getTFSPageData(this.context.pageContext);
        const project = this.pageData && this.pageData.project;
        const filter = new Filter({ defaultState: {}, useApplyMode: false });
        const actionsHub = new ActionsHub();
        this.serverSortEnabled = isFeatureFlagEnabled(context.pageContext, Constants.FeatureFlagConstants.ServerSort, false);
        this.includeFacets = !isFeatureFlagEnabled(this.context.pageContext, Constants.FeatureFlagConstants.DisableAggregations, false);

        this.preferenceSpy = new PreferenceSpy(context.pageContext, actionsHub);
        this.telemetryWriter = new TelemetryWriter(props.isTabSwitch(), context.pageContext, props.telemetryMarker);
        this.storesHub = new StoresHub(actionsHub, project, filter, props.isFullScreen(), context.pageContext, this.serverSortEnabled, this.includeFacets, props.isTabSwitch());
        this.actionCreator = new ActionCreator(
            actionsHub,
            {
                searchSource: new CodeSearchSource(context.pageContext, project),
                pageSource: new PageSource(this.context.pageContext),
                refreshInfoSource: new CodeRefreshInfoSource(this.context.pageContext)
            },
            this.storesHub.getAggregatedState,
            filter,
            context.pageContext,
            this.telemetryWriter,
            this.serverSortEnabled,
            this.includeFacets,
            props.onToggleFullScreen);

        this.telemetrySpy = new TelemetrySpy(this.telemetryWriter, actionsHub, this.storesHub);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.countSpy = new CountSpy(props.resultsCount, actionsHub);
        this.pivotStateServiceSpy = new PivotStateServiceSpy(this.context.pageContext, actionsHub);
        this.pivotStateService = this.context.pageContext.getService<ISearchPivotStateService>("ISearchPivotStateService");
        this.disposeActions = [];
    }

    public render(): JSX.Element {
        return <ResultsContainer
            actionCreator={this.actionCreator}
            storesHub={this.storesHub}
            pageContext={this.context.pageContext}
            isHosted={this.props.isHosted}
            isMember={this.props.isMember}
            isFullScreen={this.props.isFullScreen()}
        />;
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // Subscribte to various composite stores required for update commands, url, page title etc.
        this.subscribe();

        const routeParams = this.historyService.getState();

        // Update search input placeholder
        this.props.inputLabel.value = {
            inputPlaceholder: Resources.SearchCodePlaceholder,
            inputAriaLabel: this.pageData && this.pageData.project ? Resources.CodeProjectInputAriaLabel : Resources.CodeOrgInputAriaLabel
        };

        const storedFilters = this.pivotStateService.getState(EntityTypeUrlParam);
        const filters = storedFilters && storedFilters.filtersString ? storedFilters.filtersString : this.getFilters();

        // init page.
        if (routeParams.text && routeParams.text.trim()) {
            if (!this.props.isTabSwitch()) {
                const includeFacets: boolean = routeParams.includeFacets ? JSON.parse(routeParams.includeFacets) : true;
                this.actionCreator
                    .loadInitialState(
                        routeParams.text,
                        includeFacets,
                        routeParams.filters,
                        routeParams.sortOptions,
                        routeParams.result,
                        routeParams.action,
                        routeParams.lp,
                        routeParams.pageSize);

                // update page title in case search store is already inititialized.
                const searchStore = this.storesHub.getAggregatedState();
                this.updatePageTitle(searchStore.searchStoreState.query);
            } else {
                this.actionCreator
                    .loadInitialState(
                        routeParams.text,
                        this.includeFacets,
                        filters,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        routeParams.pageSize);
            }
        } else {
            this.actionCreator.showLandingPage(this.getFilters());
        }

        // Update search provider for L1 search box
        const providerId = this.pageData && this.pageData.project
            ? "ms.vss-code-web.versioncontrol-project-search-provider"
            : "ms.vss-code-web.versioncontrol-collection-search-provider";
        this.context.pageContext.getService<IVssSearchService>("IVssSearchService").setSelectedProvider(providerId);
    }

    public componentWillUnmount(): void {
        this.disposeActions.forEach(action => action());
        this.actionCreator.dispose();
        this.storesHub.dispose();
        this.telemetrySpy.dispose();
        this.preferenceSpy.dispose();
        this.countSpy.dispose();
        this.pivotStateServiceSpy.dispose();
        super.componentWillUnmount();
    }

    private onSearchQueryUpdated = ({ text, openInNewTab }: IInputQuery): void => {
        if (!openInNewTab && text) {
            this.actionCreator.applySearchText(text);
        } else if (text) {
            this.actionCreator.openSearchInNewTab(text);
        }
    }

    private getFilters(): string | undefined {
        return this.pageData && this.pageData.project
            ? serializeFilters({
                [Constants.FilterKeys.ProjectFiltersKey]: [this.pageData.project.name]
            }) : undefined;
    }

    private subscribe = (): void => {
        const notificationStore = this.storesHub.getCompositeStore(["notificationStore"]);
        const commandsCompositeStore = this.storesHub.getCompositeStore(["sortOptionsStore", "previewOrientationStore", "filterStore"]);
        const searchStore = this.storesHub.getCompositeStore(["searchStore"]);

        notificationStore.addChangedListener(this.onNotificationStoreChanged);
        commandsCompositeStore.addChangedListener(this.onCommandsUpdated);
        searchStore.addChangedListener(this.onSearchStoreUpdated);

        let urlCompositeStore: CompositeStore;
        if (this.serverSortEnabled) {
            urlCompositeStore = this.storesHub.getCompositeStore(["searchStore", "previewStore", "itemContentStore", "sortOptionsStore"]);
        } else {
            urlCompositeStore = this.storesHub.getCompositeStore(["searchStore", "previewStore", "itemContentStore"])
        }

        urlCompositeStore.addChangedListener(this.onUpdateUrl);

        this.props.inputQuery.subscribe(this.onSearchQueryUpdated);

        this.disposeActions.push(() => notificationStore.removeChangedListener(this.onNotificationStoreChanged));
        this.disposeActions.push(() => commandsCompositeStore.removeChangedListener(this.onCommandsUpdated));
        this.disposeActions.push(() => searchStore.removeChangedListener(this.onSearchStoreUpdated));
        this.disposeActions.push(() => urlCompositeStore.removeChangedListener(this.onUpdateUrl));
        this.disposeActions.push(() => this.props.inputQuery.unsubscribe(this.onSearchQueryUpdated));

        // Due to single pass render changes commandsCompositeStore doesn't get updated due to which commands aren't loaded.
        // Hence calling it explicitly once here.
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember);
    }

    private onNotificationStoreChanged = (): void => {
        handlePivotNotification(this.props.pageNotification, this.storesHub.getAggregatedState, this.actionCreator);
    }

    private onCommandsUpdated = (): void => {
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember);
    }

    private onUpdateUrl = (): void => {
        updateUrl(this.context.pageContext, this.storesHub.getAggregatedState(), this.serverSortEnabled, this.includeFacets);
    }

    private onSearchStoreUpdated = (): void => {
        const { searchStoreState } = this.storesHub.getAggregatedState();
        const { query } = searchStoreState;

        this.updatePageTitle(query);
    }

    private updatePageTitle = (query: SearchQuery): void => {
        if (query) {
            // update title of the page.
            setTimeout(() => {
                const title = getWindowTitle(Resources.SearchCodeTitle, query.searchText);
                this.context
                    .pageContext
                    .getService<ITfsNavigationService>("ITfsNavigationService")
                    .setPageTitle(title);
            }, 0);
        }
    }

    protected onNavigationOccurred = (eventData: INavigationStateChangedEvent): void => {
        applyNavigatedUrl(this.actionCreator, this.historyService.getState(), this.storesHub.getAggregatedState(), this.serverSortEnabled);
    }
}

VssComponent.register("codeSearchPivot", CodeSearchPivot);
